import {
  Component,
  computed,
  inject,
  type OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AuthService } from '../../../core/services/auth/auth.service';
import { CartService } from '../../../core/services/cart/cart.service';
import { CompanyService } from '../../../core/services/company/company.service';
import { ProductsService } from '../../../core/services/products/products.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { ThemeSelectorComponent } from '../theme-selector/theme-selector.component';
import { ButtonDirective } from '../../directives/button.directive';
import { InputDirective } from '../../directives/input.directive';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    ButtonDirective,
    FormsModule,
    InputDirective,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatToolbarModule,
    RouterLink,
    ThemeSelectorComponent,
  ],
})
export class HeaderComponent implements OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly cartService = inject(CartService);
  private readonly companyService = inject(CompanyService);
  private readonly productsService = inject(ProductsService);
  private readonly router = inject(Router);
  private readonly snackbarService = inject(SnackbarService);

  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();

  readonly company = computed(() => this.companyService.company());
  readonly whatsappUrl = computed(() => this.companyService.whatsappUrl());
  readonly user = computed(() => this.authService.user());
  readonly searchTerm = signal('');
  readonly isAuthenticated = computed(() => !!this.user());
  readonly totalItems = computed(() => this.cartService.totalItems());

  constructor() {
    this.setupSearchDebounce();
    this.checkLoginQueryParam();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLogin(): void {
    this.authService.openAuthDialog();
  }

  onLogout(): void {
    this.snackbarService.showSuccess(
      '¡Hasta luego!',
      'Has cerrado sesión correctamente',
    );
    this.authService.logout();
  }

  goToOrders(): void {
    this.router.navigate(['/user/orders']);
  }

  goToAddresses(): void {
    this.router.navigate(['/user/addresses']);
  }

  goToProfile(): void {
    this.router.navigate(['/user']);
  }

  getInitials(firstName?: string, lastName?: string): string {
    if (!firstName) return '';
    const firstInitial = firstName.charAt(0);
    const lastInitial = lastName ? lastName.charAt(0) : '';
    return (firstInitial + lastInitial).toUpperCase();
  }

  toggleCart(): void {
    this.cartService.toggleCart();
  }

  onSearch(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
    if (searchTerm) {
      this.searchSubject.next(searchTerm);
    }
  }

  triggerSearch(): void {
    const term = this.searchTerm().trim();
    if (term) {
      this.router.navigate(['/catalog'], {
        queryParams: { q: term },
        queryParamsHandling: 'merge',
      });
    }
  }

  private checkLoginQueryParam(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed())
      .subscribe((params) => {
        if (params['login'] === 'true') {
          this.onLogin();
          // Remove the query param from the URL
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { login: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
      });
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(debounceTime(500), takeUntilDestroyed())
      .subscribe((searchTerms) => {
        this.router.navigate(['/catalog'], {
          queryParams: { q: searchTerms },
          queryParamsHandling: 'merge',
        });
      });
  }
}
